import {
  Direction,
  GridTradeGraphData,
  PrecisionType,
  TradeData,
} from "@shared/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { toFixed } from "@shared/utils/numbers";
import useMedia from "utils/hooks/useMedia.ts";
import { SVGTextElementAttributes } from "react";

const useGridProgressGraph = (
  tradeData: TradeData,
  propsCurrentPriceSize: Pick<DOMRectReadOnly, "width" | "height">
) => {
  const fontSize = useMedia(
    [`(max-width: 370px)`, `(max-width: 400px)`],
    [10, 11],
    12
  );
  const linePadding = useMedia(
    [`(max-width: 380px)`, `(max-width: 400px)`],
    [6, 10],
    13
  );
  const graphData = tradeData.graph_data as GridTradeGraphData;
  const lineHeight = DEFAULT_CHART_HEIGHT - 2 * CHART_PADDING;
  const textX = LINE_X + linePadding + BG_PADDING;

  const { precision: pricePrecision } = usePrecisions({
    symbol: tradeData.symbol,
    exchange: tradeData.exchange,
    precisionType: PrecisionType.PRICE,
  });
  const {
    max_price: maxPrice,
    min_price: minPrice,
    current_price: currentPrice,
  } = graphData;
  const isReverse = tradeData["Long/Short"] === Direction.SHORT;
  const totalPriceRange = {
    min: Math.min(currentPrice, minPrice),
    max: Math.max(currentPrice, maxPrice),
  };
  const currentPriceDisplay = toFixed(currentPrice, pricePrecision);

  const convertPriceRangeToPixels = (
    priceRange: LinePriceData
  ): LinePixelData => {
    const priceDifference = totalPriceRange.max - totalPriceRange.min;
    const multiplier = 1 / (priceDifference / lineHeight);
    let height = (priceRange.max - priceRange.min) * multiplier;
    const yStart =
      LINE_START_Y + (totalPriceRange.max - priceRange.max) * multiplier;
    const yEnd = yStart + height;
    return { yStart, height, yEnd };
  };

  const extendPixelsRanges = (
    bottomRange: LinePixelData | undefined,
    topRange: LinePixelData | undefined
  ) => {
    const minRangeHeight = topRange && bottomRange ? 10 : 20;
    if (topRange && topRange.height < minRangeHeight) {
      const diff = minRangeHeight - topRange.height;
      topRange.height = minRangeHeight;
      topRange.yEnd += diff;
      if (bottomRange) {
        bottomRange.yStart += diff;
        bottomRange.height -= diff;
      }
    } else if (bottomRange && bottomRange.height < minRangeHeight) {
      const diff = minRangeHeight - bottomRange.height;
      bottomRange.height = minRangeHeight;
      bottomRange.yStart -= diff;
      if (topRange) {
        topRange.yEnd -= diff;
        topRange.height -= diff;
      }
    }
    let singleRangeAmountExtended = 0;
    if (!bottomRange || !topRange) {
      const maxHeight = lineHeight - MIN_SINGLE_RANGE_PADDING;
      if (bottomRange && bottomRange.height > maxHeight) {
        singleRangeAmountExtended = bottomRange.height - maxHeight;
        bottomRange.height = maxHeight;
        bottomRange.yStart += singleRangeAmountExtended;
      } else if (topRange && topRange.height > maxHeight) {
        singleRangeAmountExtended = topRange.height - maxHeight;
        topRange.height = maxHeight;
        topRange.yEnd -= singleRangeAmountExtended;
      }
    }
    return singleRangeAmountExtended > 0;
  };

  const getLinesData = () => {
    let topRangeData: LinePriceData | undefined;
    let bottomRangeData: LinePriceData | undefined;
    if (currentPrice > maxPrice) {
      bottomRangeData = { min: minPrice, max: maxPrice };
    } else if (currentPrice <= maxPrice && currentPrice >= minPrice) {
      bottomRangeData = { min: minPrice, max: currentPrice };
      topRangeData = { min: currentPrice, max: maxPrice };
    } else {
      topRangeData = { min: minPrice, max: maxPrice };
    }

    const bottomRangePixelData =
      bottomRangeData && convertPriceRangeToPixels(bottomRangeData);
    const topRangePixelData =
      topRangeData && convertPriceRangeToPixels(topRangeData);
    const didShrinkSingleRange = extendPixelsRanges(
      bottomRangePixelData,
      topRangePixelData
    );

    let currentPriceY: number | undefined;
    let currentPriceArrowY: number | undefined;
    let minPriceY: number | undefined;
    let maxPriceY: number | undefined;
    if (bottomRangePixelData && topRangePixelData) {
      minPriceY = bottomRangePixelData.yEnd - EDGE_TEXT_SPACING;
      maxPriceY = topRangePixelData.yStart + EDGE_TEXT_SPACING;
      currentPriceY = Math.min(
        Math.max(topRangePixelData.yEnd, 28),
        lineHeight - 15
      );
      currentPriceArrowY = topRangePixelData.yEnd;
    } else if (bottomRangePixelData) {
      minPriceY = bottomRangePixelData.yEnd - EDGE_TEXT_SPACING;
      maxPriceY = bottomRangePixelData?.yStart;
      currentPriceY = LINE_START_Y + EDGE_TEXT_SPACING + BG_PADDING;
      if (didShrinkSingleRange) {
        const multiplier =
          bottomRangePixelData.height /
          (bottomRangeData!.max - bottomRangeData!.min);
        currentPriceArrowY =
          bottomRangePixelData.yStart -
          (currentPrice - bottomRangeData!.max) * multiplier;
      }
    } else if (topRangePixelData) {
      minPriceY = topRangePixelData.yEnd;
      maxPriceY = topRangePixelData.yStart + EDGE_TEXT_SPACING;
      currentPriceY =
        LINE_START_Y + lineHeight - EDGE_TEXT_SPACING - BG_PADDING;
      if (didShrinkSingleRange) {
        const multiplier =
          topRangePixelData.height / (topRangeData!.max - topRangeData!.min);
        currentPriceArrowY =
          topRangePixelData.yEnd +
          (topRangeData!.min - currentPrice) * multiplier;
      }
    }

    return {
      greenPixelData: isReverse ? topRangePixelData : bottomRangePixelData,
      redPixelData: isReverse ? bottomRangePixelData : topRangePixelData,
      minPriceY,
      maxPriceY,
      currentPriceY,
      currentPriceArrowY,
    };
  };

  const linesData = getLinesData();
  const { currentPriceY, currentPriceArrowY } = linesData;

  const getCurrentPriceData = () => {
    const currentPriceSize = propsCurrentPriceSize;
    if (!currentPriceSize || !currentPriceY) return {};
    const x = textX - BG_PADDING;
    const y = currentPriceY - currentPriceSize.height / 2 - BG_PADDING - 1;
    const width = currentPriceSize.width + 2 * BG_PADDING;
    const height = currentPriceSize.height + 2 * BG_PADDING;
    const borderRadius = 4;

    const topLine = `M${x},${y} h${width}`;
    const topRightCorner = `a${borderRadius},${borderRadius} 0 0 1 ${borderRadius},${borderRadius}`;
    const rightLine = `v${height - 2 * borderRadius}`;
    const bottomRightCorner = `a${borderRadius},${borderRadius} 0 0 1 -${borderRadius},${borderRadius}`;
    const bottomLine = `h-${width}`;
    const rectangle = `${topLine} ${topRightCorner} ${rightLine} ${bottomRightCorner} ${bottomLine}`;

    const bottomLineY = y + height;
    const arrowYDifference = currentPriceArrowY
      ? currentPriceArrowY - bottomLineY
      : -height / 2;

    return {
      path: `${rectangle} l-${textX - 4},${arrowYDifference} z`,
      color: "#3272FE1A",
    };
  };

  const baseTextProps: SVGTextElementAttributes<SVGTextElement> = {
    x: textX,
    fontSize,
    fontWeight: 500,
    dominantBaseline: "middle",
  };

  return {
    linesData,
    getCurrentPriceData,
    minPrice: toFixed(minPrice, pricePrecision),
    maxPrice: toFixed(maxPrice, pricePrecision),
    currentPrice: currentPriceDisplay,
    baseTextProps,
    lineHeight,
  };
};

export default useGridProgressGraph;

type LinePriceData = { min: number; max: number };
type LinePixelData = {
  yStart: number;
  height: number;
  yEnd: number;
};

export const DEFAULT_CHART_HEIGHT = 90;
const CHART_PADDING = 8;
export const LINE_START_Y = CHART_PADDING;
export const LINE_X = 2;
const BG_PADDING = 2;
const EDGE_TEXT_SPACING = 3;
const MIN_SINGLE_RANGE_PADDING = 23;
